import React, { useState, MouseEvent, useEffect } from 'react'
import { Dropzone, IFileObject } from 'transfix-ui/components/Dropzone'
import { Box, Grid, Typography } from 'transfix-ui/core'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { AttachmentList } from 'apps/fleet-planner/components/AttachmentList'
import { FP_HOME } from 'apps/fleet-planner/config'
import { useActivationContext } from 'apps/fleet-planner/context'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { IFileCreate } from 'apps/fleet-planner/types/loads'
import { mapDropzoneFiles } from 'apps/fleet-planner/utils/fileUtils'
import { Button, InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui'
import { postMessageToMobileApp } from 'utils/mobileApp'
import { EMobileMessage } from './constants'

export const DeprecatedDocumentsUpload = () => {
  const { trackEvent } = useFMSAnalytics()

  useEffect(() => {
    trackEvent('Setup Guide', 'VIEW_UPLOAD_DOCUMENTS')
  }, [trackEvent])

  const {
    carrierDetails,
    errors,
    isLoading,
    mobileApp,
    sendDocumentEmail,
    sendPacketEmail,
    setShowActivationToast,
    submitPackageFiles,
  } = useActivationContext()
  const isFmsBcaIncreaseUploadLimit = useFeatureFlag('fmsBcaIncreaseUploadLimit')
  const { isMobile } = useBreakpointContext()
  const { id } = carrierDetails || {}
  const [files, setFiles] = useState<IFileCreate[]>([])

  const fileChangeHandler = (docs: IFileObject[]) => {
    if (docs) {
      const pkgFiles = mapDropzoneFiles(docs)
      if (pkgFiles) setFiles([...files, ...pkgFiles])
    }
  }

  const fileDeleteHandler = (event: MouseEvent<HTMLButtonElement>) => {
    const selectUuid = event?.currentTarget.id
    if (selectUuid) {
      setFiles(files?.filter(file => file.uuid !== selectUuid))
    }
  }

  const enrollmentSuccessHandler = () => {
    setShowActivationToast(true)
    postMessageToMobileApp(mobileApp, EMobileMessage.DOCUMENT_UPLOAD_COMPLETE)
    window.location.replace(`${__ROOT__}/fms${FP_HOME}`)
  }

  const skipHandler = () => {
    trackEvent('Setup Guide', 'SKIPPED_UPLOAD_DOCUMENTS')
    postMessageToMobileApp(mobileApp, EMobileMessage.SKIPPED_DOCUMENT_UPLOAD)
    window.location.replace(`${__ROOT__}/fms${FP_HOME}`)
  }

  const submitHandler = async () => {
    const response = await submitPackageFiles(id, { packageFiles: files })
    const { packageFiles = [] } = response || {}
    if (packageFiles.length) {
      trackEvent('Setup Guide', 'UPLOAD_DOCUMENTS_SUCCESS')
      sendPacketEmail(id)
      sendDocumentEmail(id)
      enrollmentSuccessHandler()
    } else {
      trackEvent('Setup Guide', 'UPLOAD_DOCUMENTS_FAILURE')
    }
  }

  return (
    <Box mt={4}>
      <Box>
        <Grid container alignItems='center' justifyContent='center'>
          <Grid item xs={12} md={8} lg={6}>
            <Typography variant='h3'>Upload your documents</Typography>
          </Grid>
        </Grid>
      </Box>
      <Box mt={1}>
        <Grid container alignItems='center' justifyContent='center'>
          <Grid item xs={12} md={8} lg={6}>
            <Typography variant='subtitle2'>You can upload them now or later.</Typography>
          </Grid>
        </Grid>
      </Box>
      <Box mt={4}>
        <Grid container alignItems='center' justifyContent='center'>
          <Grid item xs={12} md={8} lg={6}>
            <Typography variant='subtitle1'>Please include:</Typography>
            <Box paddingLeft={1} marginTop={1}>
              <Box>
                <Typography variant='body2'>&#8226; W-9</Typography>
              </Box>
              <Box>
                <Typography variant='body2'>&#8226; Operating Authority</Typography>
              </Box>
              <Box>
                <Typography variant='body2'>&#8226; Certificate of Insurance</Typography>
              </Box>
              <Box>
                <Typography variant='body2'>
                  &#8226; Factoring company information (if applicable)
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Box mt={4}>
        <Grid container alignItems='center' justifyContent='center'>
          <Grid item xs={12} md={8} lg={6}>
            {files.length > 0 && (
              <Box mb={1.5}>
                <AttachmentList showIcon attachments={files} onFileDelete={fileDeleteHandler} />
              </Box>
            )}
            <Box>
              {isFmsBcaIncreaseUploadLimit ? (
                <Dropzone
                  dropZoneText='Upload a file or drop it here'
                  filesLimit={7}
                  onFileChange={fileChangeHandler}
                />
              ) : (
                <Dropzone
                  dropZoneText='Upload a file or drop it here'
                  onFileChange={fileChangeHandler}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
      {errors && (
        <Box mt={8}>
          <Grid item xs={12}>
            <InlineMessage type={INLINE_MESSAGE_VARIANTS.ALERT} message={errors} />
          </Grid>
        </Box>
      )}
      <Grid container alignItems='center' justifyContent='center'>
        <Grid item xs={12} md={8} lg={6}>
          <Box textAlign='right' mt={4}>
            <Grid container justifyContent='flex-end'>
              <Grid item xs={12} md={4}>
                <Box pt={isMobile ? 2 : 0}>
                  <Button
                    data-test='submit-documents-button'
                    fullWidth
                    size='large'
                    onClick={submitHandler}
                    disabled={files.length < 1 || isLoading}>
                    {isLoading ? 'Loading...' : 'Submit'}
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  data-test='skip-documents-button'
                  fullWidth
                  color='tertiary'
                  size='large'
                  onClick={skipHandler}>
                  Skip and Send Later
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Box>
  )
}
